import React from 'react';
import { motion } from 'framer-motion';

// The 3 milestones along the growth curve - positioned ABOVE the curve line
const milestones = [
  {
    phase: 'MIRROR',
    days: 'Days 1-7',
    benefit: 'Awareness & Baseline',
    position: { x: 38, y: 62 }, // Bottom-left, near curve start
    labelPosition: 'right', // Label on right side of dot
  },
  {
    phase: 'MENTOR', 
    days: 'Days 8-14',
    benefit: 'Pattern Correction',
    position: { x: 58, y: 32 }, // Middle, left side of the curve
    labelPosition: 'left', // Label on left side of dot
  },
  {
    phase: 'MASTER',
    days: 'Days 15-21',
    benefit: 'Validated Execution',
    position: { x: 82, y: 24 }, // Below header
    labelPosition: 'bottom', // Label below the dot
  },
];

// SVG path for smooth growth curve - starts at (5%, 82%) ends at (95%, 18%)
const growthCurvePath = "M 5 82 Q 25 70, 40 55 Q 55 38, 70 28 Q 82 20, 95 18";

export default function ClarityAnimation() {
  return (
    <div 
      data-testid="clarity-animation"
      className="relative w-full h-full min-h-[280px] rounded-2xl overflow-hidden bg-gradient-to-br from-blue-50 via-slate-50 to-emerald-50"
    >
      {/* Background subtle grid */}
      <div className="absolute inset-0 opacity-30">
        <svg className="w-full h-full">
          <defs>
            <pattern id="gridPattern" width="30" height="30" patternUnits="userSpaceOnUse">
              <path d="M 30 0 L 0 0 0 30" fill="none" stroke="rgba(37,99,235,0.15)" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#gridPattern)" />
        </svg>
      </div>

      {/* Ambient glow */}
      <div className="absolute top-1/3 right-1/3 w-32 h-32 bg-blue-400/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 left-1/4 w-24 h-24 bg-emerald-400/10 rounded-full blur-2xl" />

      {/* Header - Positioned at TOP RIGHT corner */}
      <motion.div 
        className="absolute top-3 right-4 text-right z-10"
        initial={{ opacity: 0, x: 10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.2 }}
      >
        <span className="text-[10px] md:text-xs font-semibold text-blue-600 uppercase tracking-wider">After</span>
        <p className="text-sm md:text-lg font-bold text-slate-800">21 Days to Clarity</p>
      </motion.div>

      {/* Growth Curve SVG */}
      <svg 
        className="absolute inset-0 w-full h-full" 
        viewBox="0 0 100 100" 
        preserveAspectRatio="none"
      >
        {/* Gradient definition for curve */}
        <defs>
          <linearGradient id="curveGradientLight" x1="0%" y1="100%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgba(37,99,235,0.9)" />
            <stop offset="50%" stopColor="rgba(59,130,246,0.9)" />
            <stop offset="100%" stopColor="rgba(16,185,129,0.9)" />
          </linearGradient>
          <linearGradient id="areaGradientLight" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="rgba(37,99,235,0.15)" />
            <stop offset="100%" stopColor="rgba(37,99,235,0)" />
          </linearGradient>
        </defs>

        {/* Area under curve */}
        <motion.path
          d={growthCurvePath + " L 95 92 L 5 92 Z"}
          fill="url(#areaGradientLight)"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
        />

        {/* Main growth curve line */}
        <motion.path
          d={growthCurvePath}
          fill="none"
          stroke="url(#curveGradientLight)"
          strokeWidth="2.5"
          strokeLinecap="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
        />
      </svg>

      {/* Milestone Points Along the Curve - TEXT ONLY, NO ICONS */}
      {milestones.map((milestone, index) => {
        const colors = [
          { text: 'text-blue-700', dot: 'bg-blue-500', border: 'border-blue-300' },
          { text: 'text-blue-700', dot: 'bg-blue-600', border: 'border-blue-300' },
          { text: 'text-emerald-700', dot: 'bg-emerald-500', border: 'border-emerald-300' },
        ][index];

        // Position labels based on milestone's labelPosition property
        let labelPositionClass = 'left-full ml-3 top-1/2 -translate-y-1/2'; // default: right
        if (milestone.labelPosition === 'left') {
          labelPositionClass = 'right-full mr-3 top-1/2 -translate-y-1/2';
        } else if (milestone.labelPosition === 'bottom') {
          labelPositionClass = 'top-full mt-2 left-1/2 -translate-x-1/2';
        }

        return (
          <motion.div
            key={milestone.phase}
            className="absolute z-10"
            style={{ 
              left: `${milestone.position.x}%`, 
              top: `${milestone.position.y}%`,
              transform: 'translate(-50%, -50%)'
            }}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ 
              delay: 0.8 + index * 0.3,
              type: 'spring',
              stiffness: 200,
              damping: 15
            }}
          >
            {/* Milestone point - Simple dot */}
            <div className="relative group">
              {/* Pulse ring */}
              <motion.div
                className={`absolute inset-0 w-4 h-4 md:w-5 md:h-5 rounded-full ${colors.dot}/30`}
                style={{ transform: 'translate(-25%, -25%)' }}
                animate={{ scale: [1, 1.8, 1], opacity: [0.5, 0, 0.5] }}
                transition={{ duration: 2.5, repeat: Infinity, delay: index * 0.4 }}
              />
              
              {/* Dot on curve */}
              <div className={`w-3 h-3 md:w-4 md:h-4 rounded-full ${colors.dot} shadow-md`} />

              {/* Label card - WHITE with shadow */}
              <div className={`absolute ${labelPositionClass} whitespace-nowrap`}>
                <div className="bg-white rounded-lg px-2.5 py-1.5 border border-slate-200 shadow-md">
                  <div className={`text-[11px] md:text-xs font-bold ${colors.text}`}>
                    {milestone.phase}
                  </div>
                  <div className="text-[9px] md:text-[10px] text-slate-500">
                    {milestone.days}
                  </div>
                  <div className="text-[9px] md:text-[10px] text-slate-600 mt-0.5">
                    {milestone.benefit}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        );
      })}

      {/* X-axis labels */}
      <div className="absolute bottom-3 left-4 right-4 flex justify-between items-center">
        <span className="text-[9px] md:text-[10px] text-slate-500">Day 1</span>
        <motion.span 
          className="text-[9px] md:text-[10px] text-emerald-600 font-medium"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8 }}
        >
          Your growth starts here
        </motion.span>
      </div>
    </div>
  );
}
